import { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';
import { Newspaper, Calendar, User, ArrowRight, Tag, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const { language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const getText = (key) => {
    const texts = {
      title: {
        fr: 'Blog & Actualités',
        en: 'Blog & News',
        uk: 'Блог та новини',
        ru: 'Блог и новости',
        it: 'Blog & Notizie',
        ro: 'Blog & Știri',
        ka: 'ბლოგი და სიახლეები'
      },
      subtitle: {
        fr: 'Découvrez nos derniers articles sur la santé, le bien-être et les médecines douces',
        en: 'Discover our latest articles on health, wellness and natural medicine',
        uk: 'Відкрийте наші останні статті про здоров\'я, благополуччя та натуральну медицину',
        ru: 'Откройте наши последние статьи о здоровье, благополучии и натуральной медицине',
        it: 'Scopri i nostri ultimi articoli sulla salute, il benessere e la medicina naturale',
        ro: 'Descoperă ultimele noastre articole despre sănătate, bunăstare și medicină naturală',
        ka: 'აღმოაჩინეთ ჩვენი უახლესი სტატიები ჯანმრთელობის, კეთილდღეობისა და ბუნებრივი მედიცინის შესახებ'
      },
      searchPlaceholder: {
        fr: 'Rechercher un article...',
        en: 'Search articles...',
        uk: 'Пошук статей...',
        ru: 'Поиск статей...',
        it: 'Cerca articoli...',
        ro: 'Caută articole...',
        ka: 'სტატიების ძებნა...'
      },
      allCategories: {
        fr: 'Toutes les catégories',
        en: 'All categories',
        uk: 'Усі категорії',
        ru: 'Все категории',
        it: 'Tutte le categorie',
        ro: 'Toate categoriile',
        ka: 'ყველა კატეგორია'
      },
      readArticle: {
        fr: 'Lire l\'article',
        en: 'Read article',
        uk: 'Читати статтю',
        ru: 'Читать статью',
        it: 'Leggi articolo',
        ro: 'Citește articolul',
        ka: 'სტატიის წაკითხვა'
      },
      comingSoon: {
        fr: 'Articles à venir bientôt',
        en: 'Articles coming soon',
        uk: 'Статті незабаром',
        ru: 'Статьи скоро',
        it: 'Articoli in arrivo',
        ro: 'Articole în curând',
        ka: 'სტატიები მალე'
      },
      noArticles: {
        fr: 'Aucun article trouvé',
        en: 'No articles found',
        uk: 'Статей не знайдено',
        ru: 'Статей не найдено',
        it: 'Nessun articolo trovato',
        ro: 'Niciun articol găsit',
        ka: 'სტატიები ვერ მოიძებნა'
      },
      stayTuned: {
        fr: 'Restez informé',
        en: 'Stay informed',
        uk: 'Будьте в курсі',
        ru: 'Будьте в курсе',
        it: 'Resta informato',
        ro: 'Rămâi informat',
        ka: 'იყავით ინფორმირებული'
      },
      newsletter: {
        fr: 'Inscrivez-vous à notre newsletter pour recevoir les dernières actualités',
        en: 'Subscribe to our newsletter to receive the latest news',
        uk: 'Підпишіться на нашу розсилку, щоб отримувати останні новини',
        ru: 'Подпишитесь на нашу рассылку, чтобы получать последние новости',
        it: 'Iscriviti alla nostra newsletter per ricevere le ultime notizie',
        ro: 'Abonează-te la newsletter-ul nostru pentru a primi ultimele noutăți',
        ka: 'გამოიწერეთ ჩვენი სიახლეების ბიულეტენი უახლესი ამბების მისაღებად'
      }
    };
    return texts[key]?.[language] || texts[key]?.['fr'] || key;
  };

  const categories = [
    { id: 'all', name: { fr: 'Tous', en: 'All', uk: 'Усі', ru: 'Все', it: 'Tutti', ro: 'Toate', ka: 'ყველა' } },
    { id: 'wellness', name: { fr: 'Bien-être', en: 'Wellness', uk: 'Благополуччя', ru: 'Благополучие', it: 'Benessere', ro: 'Bunăstare', ka: 'კეთილდღეობა' } },
    { id: 'health', name: { fr: 'Santé', en: 'Health', uk: 'Здоров\'я', ru: 'Здоровье', it: 'Salute', ro: 'Sănătate', ka: 'ჯანმრთელობა' } },
    { id: 'testimonials', name: { fr: 'Témoignages', en: 'Testimonials', uk: 'Відгуки', ru: 'Отзывы', it: 'Testimonianze', ro: 'Mărturii', ka: 'მოწმობები' } },
    { id: 'news', name: { fr: 'Actualités', en: 'News', uk: 'Новини', ru: 'Новости', it: 'Attualità', ro: 'Actualități', ka: 'სიახლეები' } }
  ];

  // Default articles (fallback if API returns nothing)
  const defaultArticles = [
    {
      id: 1,
      slug: 'bienfaits-naturopathie',
      title: {
        fr: 'Les bienfaits de la naturopathie pour les expatriés',
        en: 'Benefits of Naturopathy for Expatriates',
        uk: 'Переваги натуропатії для експатріантів',
        ru: 'Преимущества натуропатии для экспатриантов',
        it: 'I benefici della naturopatia per gli espatriati',
        ro: 'Beneficiile naturopatiei pentru expatriați',
        ka: 'ნატუროპათიის სარგებელი ემიგრანტებისთვის'
      },
      excerpt: {
        fr: 'Découvrez comment la naturopathie peut aider les expatriés à gérer le stress de l\'adaptation et maintenir leur équilibre.',
        en: 'Discover how naturopathy can help expatriates manage adaptation stress and maintain their balance.',
        uk: 'Дізнайтеся, як натуропатія може допомогти експатріантам керувати стресом адаптації та підтримувати баланс.',
        ru: 'Узнайте, как натуропатия может помочь экспатриантам справиться со стрессом адаптации и поддержать баланс.',
        it: 'Scopri come la naturopatia può aiutare gli espatriati a gestire lo stress dell\'adattamento e mantenere il loro equilibrio.',
        ro: 'Descoperă cum naturopatia poate ajuta expatriații să gestioneze stresul adaptării și să-și mențină echilibrul.',
        ka: 'აღმოაჩინეთ როგორ შეუძლია ნატუროპათიას დაეხმაროს ემიგრანტებს ადაპტაციის სტრესის მართვაში და ბალანსის შენარჩუნებაში.'
      },
      image: '/images/blog/naturopathy.jpg',
      category: 'wellness',
      author: 'Dr. Marie Laurent',
      date: '2026-03-15',
      readTime: '5 min'
    },
    {
      id: 2,
      slug: 'telemedecine-internationale',
      title: {
        fr: 'La télémédecine : révolution pour les patients internationaux',
        en: 'Telemedicine: Revolution for International Patients',
        uk: 'Телемедицина: революція для міжнародних пацієнтів',
        ru: 'Телемедицина: революция для международных пациентов',
        it: 'La telemedicina: rivoluzione per i pazienti internazionali',
        ro: 'Telemedicina: revoluție pentru pacienții internaționali',
        ka: 'ტელემედიცინა: რევოლუცია საერთაშორისო პაციენტებისთვის'
      },
      excerpt: {
        fr: 'Comment la visioconférence transforme l\'accès aux soins pour les patients à travers le monde.',
        en: 'How video conferencing is transforming access to care for patients around the world.',
        uk: 'Як відеоконференції трансформують доступ до медичної допомоги для пацієнтів по всьому світу.',
        ru: 'Как видеоконференции трансформируют доступ к медицинской помощи для пациентов по всему миру.',
        it: 'Come la videoconferenza sta trasformando l\'accesso alle cure per i pazienti in tutto il mondo.',
        ro: 'Cum videoconferința transformă accesul la îngrijire pentru pacienții din întreaga lume.',
        ka: 'როგორ გარდაქმნის ვიდეოკონფერენცია ჯანდაცვაზე წვდომას პაციენტებისთვის მთელ მსოფლიოში.'
      },
      image: '/images/blog/telemedicine.jpg',
      category: 'health',
      author: 'Équipe Opora Vie',
      date: '2026-03-10',
      readTime: '4 min'
    },
    {
      id: 3,
      slug: 'temoignage-olga-ukraine',
      title: {
        fr: 'Témoignage : Olga, de Kiev à Paris',
        en: 'Testimonial: Olga, from Kyiv to Paris',
        uk: 'Відгук: Ольга, з Києва до Парижа',
        ru: 'Отзыв: Ольга, из Киева в Париж',
        it: 'Testimonianza: Olga, da Kiev a Parigi',
        ro: 'Mărturie: Olga, de la Kiev la Paris',
        ka: 'მოწმობა: ოლგა, კიევიდან პარიზში'
      },
      excerpt: {
        fr: 'Le parcours d\'Olga qui a trouvé un sophrologue ukrainien à Paris pour l\'aider dans sa transition.',
        en: 'Olga\'s journey finding a Ukrainian sophrologist in Paris to help with her transition.',
        uk: 'Шлях Ольги, яка знайшла українського софролога в Парижі, щоб допомогти з її переходом.',
        ru: 'Путь Ольги, которая нашла украинского софролога в Париже, чтобы помочь с её переездом.',
        it: 'Il percorso di Olga che ha trovato una sofrologa ucraina a Parigi per aiutarla nella sua transizione.',
        ro: 'Călătoria Olgăi care a găsit un sofrolog ucrainean la Paris pentru a o ajuta în tranziția ei.',
        ka: 'ოლგას გზა, რომელმაც უკრაინელი სოფროლოგი იპოვა პარიზში მისი ადაპტაციის დასახმარებლად.'
      },
      image: '/images/blog/testimonial.jpg',
      category: 'testimonials',
      author: 'Rédaction',
      date: '2026-03-05',
      readTime: '6 min'
    },
    {
      id: 4,
      slug: 'nouvelles-specialites-2026',
      title: {
        fr: 'Nouvelles spécialités disponibles en 2026',
        en: 'New Specialties Available in 2026',
        uk: 'Нові спеціальності доступні в 2026 році',
        ru: 'Новые специальности доступны в 2026 году',
        it: 'Nuove specialità disponibili nel 2026',
        ro: 'Noi specialități disponibile în 2026',
        ka: 'ახალი სპეციალობები ხელმისაწვდომია 2026 წელს'
      },
      excerpt: {
        fr: 'Opora Vie élargit son réseau avec de nouvelles disciplines de médecines douces.',
        en: 'Opora Vie expands its network with new natural medicine disciplines.',
        uk: 'Opora Vie розширює свою мережу новими дисциплінами натуральної медицини.',
        ru: 'Opora Vie расширяет свою сеть новыми дисциплинами натуральной медицины.',
        it: 'Opora Vie espande la sua rete con nuove discipline di medicina naturale.',
        ro: 'Opora Vie își extinde rețeaua cu noi discipline de medicină naturală.',
        ka: 'Opora Vie აფართოებს თავის ქსელს ბუნებრივი მედიცინის ახალი დისციპლინებით.'
      },
      image: '/images/blog/news.jpg',
      category: 'news',
      author: 'Équipe Opora Vie',
      date: '2026-02-28',
      readTime: '3 min'
    }
  ];

  // Fetch articles from API on mount
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await axios.get(`${API}/blog/articles`);
        if (res.data && res.data.length > 0) {
          // Transform API articles to match the component format
          const apiArticles = res.data.map(article => ({
            id: article.id,
            slug: article.slug,
            title: {
              fr: article.title_fr || '',
              en: article.title_en || article.title_fr || '',
              uk: article.title_uk || article.title_fr || '',
              ru: article.title_ru || article.title_fr || '',
              it: article.title_it || article.title_fr || '',
              ro: article.title_ro || article.title_fr || ''
            },
            excerpt: {
              fr: article.excerpt_fr || '',
              en: article.excerpt_en || article.excerpt_fr || '',
              uk: article.excerpt_uk || article.excerpt_fr || '',
              ru: article.excerpt_ru || article.excerpt_fr || '',
              it: article.excerpt_it || article.excerpt_fr || '',
              ro: article.excerpt_ro || article.excerpt_fr || ''
            },
            image: article.image_url || '/images/blog/default.jpg',
            category: article.category,
            author: article.author || 'Équipe Opora Vie',
            date: article.created_at ? article.created_at.split('T')[0] : '2026-01-01',
            readTime: article.read_time || '5 min'
          }));
          setArticles(apiArticles);
        } else {
          // Use default articles if API returns empty
          setArticles(defaultArticles);
        }
      } catch (error) {
        console.log('Using default articles');
        setArticles(defaultArticles);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const filteredArticles = articles.filter(article => {
    const titleText = article.title[language] || article.title.fr || '';
    const excerptText = article.excerpt[language] || article.excerpt.fr || '';
    const matchesSearch = searchTerm === '' || 
      titleText.toLowerCase().includes(searchTerm.toLowerCase()) ||
      excerptText.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const locale = language === 'uk' ? 'uk-UA' : language === 'ru' ? 'ru-RU' : language === 'en' ? 'en-US' : 'fr-FR';
    return date.toLocaleDateString(locale, options);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="blog-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 py-16">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center">
            <Newspaper className="w-16 h-16 text-amber-500 mx-auto mb-6" />
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              {getText('title')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {getText('subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="container-app py-8">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={getText('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
                className={selectedCategory === cat.id ? 'bg-sage-500 hover:bg-sage-600' : ''}
              >
                {cat.name[language] || cat.name.fr}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="container-app py-8">
        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map(article => (
              <article 
                key={article.id}
                className="bg-white rounded-2xl border border-sand-200 overflow-hidden shadow-soft hover:shadow-md transition-all group"
              >
                <div className="aspect-video bg-gradient-to-br from-sage-100 to-sand-100 flex items-center justify-center">
                  <Newspaper className="w-16 h-16 text-sage-300" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-sage-100 text-sage-700">
                      {categories.find(c => c.id === article.category)?.name[language] || article.category}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2 group-hover:text-sage-600 transition-colors line-clamp-2">
                    {article.title[language] || article.title.fr}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                    {article.excerpt[language] || article.excerpt.fr}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {article.author}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(article.date)}
                    </div>
                  </div>
                </div>
                <div className="px-5 pb-5">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full group-hover:bg-sage-50 group-hover:border-sage-200"
                    disabled
                  >
                    {getText('readArticle')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Newspaper className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-medium mb-2">{getText('noArticles')}</h3>
            <p className="text-muted-foreground">{getText('comingSoon')}</p>
          </div>
        )}
      </div>

      {/* Newsletter CTA */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-12">
        <div className="container-app text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-3">
            {getText('stayTuned')}
          </h2>
          <p className="text-amber-100 mb-6 max-w-lg mx-auto">
            {getText('newsletter')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input 
              type="email" 
              placeholder="Email" 
              className="bg-white/90 border-0 rounded-xl"
            />
            <Button className="bg-white text-amber-600 hover:bg-amber-50 whitespace-nowrap">
              {language === 'uk' ? 'Підписатися' : language === 'ru' ? 'Подписаться' : language === 'en' ? 'Subscribe' : 'S\'inscrire'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
